// app/frontend/components/videos/FilterSidebar.tsx
import React from "react";
import {
  Stack,
  Box,
  Title,
  Text,
  NavLink,
  Divider,
  Collapse,
  UnstyledButton,
  Group,
  Button,
  ScrollArea,
  Checkbox,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import type { LearningTrack, FilterOptions } from "../../types/video";

interface FilterSidebarProps {
  learningTracks: LearningTrack[];
  filters: FilterOptions;
  selectedCategory: string | null;
  selectedTrack: number | null;
  selectedSkillLevels: string[];
  selectedTags: string[];
  onCategoryChange: (category: string | null) => void;
  onTrackChange: (trackId: number | null) => void;
  onSkillLevelToggle: (skillLevel: string) => void;
  onTagToggle: (tag: string) => void;
  onClearAll: () => void;
  videoCounts?: Record<string, number>;
}

const FilterSidebar: React.FC<FilterSidebarProps> = ({
  learningTracks,
  filters,
  selectedCategory,
  selectedTrack,
  selectedSkillLevels,
  selectedTags,
  onCategoryChange,
  onTrackChange,
  onSkillLevelToggle,
  onTagToggle,
  onClearAll,
  videoCounts = {},
}) => {
  const [categoriesOpened, { toggle: toggleCategories }] = useDisclosure(false);
  const [tracksOpened, { toggle: toggleTracks }] = useDisclosure(true);
  const [skillLevelsOpened, { toggle: toggleSkillLevels }] =
    useDisclosure(false);
  const [tagsOpened, { toggle: toggleTags }] = useDisclosure(false);

  const groupedSkillLevels = {
    Beginner: filters.skill_levels.filter((lvl) => lvl.includes("Beginner")),
    Improver: filters.skill_levels.filter((lvl) => lvl.includes("Improver")),
    Intermediate: filters.skill_levels.filter((lvl) =>
      lvl.includes("Intermediate")
    ),
    Advanced: filters.skill_levels.filter((lvl) => lvl.includes("Advanced")),
  };

  const getSkillLevelColor = (lvl: string) => {
    if (lvl.includes("Beginner")) return "green";
    if (lvl.includes("Improver")) return "blue";
    if (lvl.includes("Intermediate")) return "orange";
    if (lvl.includes("Advanced")) return "red";
    return "gray";
  };

  return (
    <Box
      style={{
        background: "rgba(0, 0, 0, 0.6)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        backdropFilter: "blur(15px)",
        height: "100vh",
        width: "300px",
      }}
      p="md"
    >
      <ScrollArea style={{ height: "calc(100vh - 100px)" }}>
        <Stack gap="md">
          {/* Categories */}
          <Box>
            <UnstyledButton
              onClick={toggleCategories}
              style={{ width: "100%" }}
            >
              <Group justify="space-between" mb="xs">
                <Box>
                  <Title
                    order={4}
                    c="#F0F0F0"
                    style={{
                      fontFamily: "'Big Shoulders Display', sans-serif",
                      fontWeight: 600,
                    }}
                  >
                    Categories
                  </Title>
                  <Text
                    size="xs"
                    c="#B8B8B8"
                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                  >
                    Choose one category
                  </Text>
                </Box>
                {categoriesOpened ? (
                  <IconChevronDown size={16} color="#FF9800" />
                ) : (
                  <IconChevronRight size={16} color="#FF9800" />
                )}
              </Group>
            </UnstyledButton>
            <Collapse in={categoriesOpened}>
              <Stack gap="xs">
                <NavLink
                  label="All Categories"
                  active={selectedCategory === null}
                  onClick={() => onCategoryChange(null)}
                  color="orange"
                  styles={{
                    root: {
                      color: "#D0D0D0",
                      fontFamily: "'Space Grotesk', sans-serif",
                    },
                    label: {
                      color: selectedCategory === null ? "#FF9800" : "#D0D0D0",
                    },
                  }}
                />
                {filters.categories.map((category) => (
                  <NavLink
                    key={category}
                    label={category}
                    description={`${videoCounts[category] || 0} videos`}
                    active={selectedCategory === category}
                    onClick={() => onCategoryChange(category)}
                    styles={{
                      root: {
                        color:
                          selectedCategory === category ? "#FF9800" : "#D0D0D0",
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontWeight: 600,
                      },
                      label: {
                        color:
                          selectedCategory === category ? "#FF9800" : "#D0D0D0",
                      },
                      description: { color: "#B8B8B8" },
                    }}
                  />
                ))}
              </Stack>
            </Collapse>
          </Box>

          <Divider color="rgba(255, 152, 0, 0.3)" />

          {/* Learning Tracks */}
          <Box>
            <UnstyledButton onClick={toggleTracks} style={{ width: "100%" }}>
              <Group justify="space-between" mb="xs">
                <Box>
                  <Title
                    order={4}
                    c="#F0F0F0"
                    style={{
                      fontFamily: "'Big Shoulders Display', sans-serif",
                      fontWeight: 600,
                    }}
                  >
                    Learning Tracks
                  </Title>
                  <Text
                    size="xs"
                    c="#B8B8B8"
                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                  >
                    Choose one learning path
                  </Text>
                </Box>
                {tracksOpened ? (
                  <IconChevronDown size={16} color="#FF9800" />
                ) : (
                  <IconChevronRight size={16} color="#FF9800" />
                )}
              </Group>
            </UnstyledButton>
            <Collapse in={tracksOpened}>
              <Stack gap="xs">
                <NavLink
                  label="All Videos"
                  active={selectedTrack === null}
                  onClick={() => onTrackChange(null)}
                  color="orange"
                  styles={{
                    root: {
                      color: "#D0D0D0",
                      fontFamily: "'Space Grotesk', sans-serif",
                    },
                    label: {
                      color: selectedTrack === null ? "#FF9800" : "#D0D0D0",
                    },
                  }}
                />
                {learningTracks.map((track) => (
                  <NavLink
                    key={track.id}
                    label={track.name}
                    description={`${track.estimated_duration} • ${track.difficulty_range}`}
                    active={selectedTrack === track.id}
                    onClick={() => onTrackChange(track.id)}
                    color="orange"
                    styles={{
                      root: {
                        color: "#D0D0D0",
                        fontFamily: "'Space Grotesk', sans-serif",
                      },
                      label: {
                        color:
                          selectedTrack === track.id ? "#FF9800" : "#D0D0D0",
                      },
                      description: { color: "#B8B8B8" },
                    }}
                  />
                ))}
              </Stack>
            </Collapse>
          </Box>

          <Divider color="rgba(255, 152, 0, 0.3)" />

          {/* Skill Levels */}
          <Box>
            <UnstyledButton
              onClick={toggleSkillLevels}
              style={{ width: "100%" }}
            >
              <Group justify="space-between" mb="xs">
                <Box>
                  <Title
                    order={4}
                    c="#F0F0F0"
                    style={{
                      fontFamily: "'Big Shoulders Display', sans-serif",
                      fontWeight: 600,
                    }}
                  >
                    Skill Levels
                  </Title>
                  <Text
                    size="xs"
                    c="#B8B8B8"
                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                  >
                    Select multiple levels
                  </Text>
                </Box>
                {skillLevelsOpened ? (
                  <IconChevronDown size={16} color="#FF9800" />
                ) : (
                  <IconChevronRight size={16} color="#FF9800" />
                )}
              </Group>
            </UnstyledButton>
            <Collapse in={skillLevelsOpened}>
              <Stack gap="xs">
                {Object.entries(groupedSkillLevels).map(([group, levels]) => (
                  <Box key={group}>
                    <Text
                      size="sm"
                      fw={500}
                      c="#B8B8B8"
                      mb="xs"
                      ml="sm"
                      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                    >
                      {group}
                    </Text>
                    {levels.map((level) => (
                      <Checkbox
                        key={level}
                        label={level}
                        checked={selectedSkillLevels.includes(level)}
                        onChange={() => onSkillLevelToggle(level)}
                        color={getSkillLevelColor(level)}
                        ml="md"
                        styles={{
                          label: {
                            color: selectedSkillLevels.includes(level)
                              ? "#FF9800"
                              : "#D0D0D0",
                            fontFamily: "'Space Grotesk', sans-serif",
                          },
                          input: {
                            backgroundColor: "rgba(255, 255, 255, 0.05)",
                            borderColor: "rgba(255, 152, 0, 0.3)",
                          },
                        }}
                      />
                    ))}
                  </Box>
                ))}
              </Stack>
            </Collapse>
          </Box>

          <Divider color="rgba(255, 152, 0, 0.3)" />

          {/* Tags */}
          <Box>
            <UnstyledButton onClick={toggleTags} style={{ width: "100%" }}>
              <Group justify="space-between" mb="xs">
                <Box>
                  <Title
                    order={4}
                    c="#F0F0F0"
                    style={{
                      fontFamily: "'Big Shoulders Display', sans-serif",
                      fontWeight: 600,
                    }}
                  >
                    Tags
                  </Title>
                  <Text
                    size="xs"
                    c="#B8B8B8"
                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                  >
                    Select multiple tags
                  </Text>
                </Box>
                {tagsOpened ? (
                  <IconChevronDown size={16} color="#FF9800" />
                ) : (
                  <IconChevronRight size={16} color="#FF9800" />
                )}
              </Group>
            </UnstyledButton>
            <Collapse in={tagsOpened}>
              <Stack gap="xs">
                {filters.tags.map((tag) => (
                  <Checkbox
                    key={tag}
                    label={tag}
                    checked={selectedTags.includes(tag)}
                    onChange={() => onTagToggle(tag)}
                    color="orange"
                    styles={{
                      label: {
                        color: selectedTags.includes(tag)
                          ? "#FF9800"
                          : "#D0D0D0",
                        fontFamily: "'Space Grotesk', sans-serif",
                      },
                      input: {
                        backgroundColor: "rgba(255, 255, 255, 0.05)",
                        borderColor: "rgba(255, 152, 0, 0.3)",
                        "&:checked": {
                          backgroundColor: "#FF9800",
                          borderColor: "#FF9800",
                        },
                      },
                    }}
                  />
                ))}
              </Stack>
            </Collapse>
          </Box>

          <Button
            variant="outline"
            fullWidth
            size="sm"
            onClick={onClearAll}
            style={{
              borderColor: "#FF9800",
              color: "#FF9800",
              fontFamily: "'Big Shoulders Display', sans-serif",
              fontWeight: 600,
              marginTop: "auto",
            }}
          >
            Clear All Filters
          </Button>
        </Stack>
      </ScrollArea>
    </Box>
  );
};

export default FilterSidebar;
